import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutGrid,
  Hammer,
  CalendarRange,
  BookOpen,
  Gamepad2,
  Settings,
  Sparkles,
  CheckSquare,
  FileCode,
  Shield,
} from "lucide-react";

const NAV_ITEMS = [
  {
    to: "/",
    label: "Overview",
    icon: LayoutGrid,
    end: true,
    testid: "nav-home",
    accent: "slate",
  },
  {
    to: "/forge",
    label: "App Forge",
    icon: Hammer,
    testid: "nav-app-forge",
    accent: "indigo",
    subtitle: "custom-app-forge-14",
  },
  {
    to: "/calendar",
    label: "Calendar · Maps",
    icon: CalendarRange,
    testid: "nav-calendar-maps",
    accent: "emerald",
    subtitle: "calendar-maps-pod",
  },
  {
    to: "/quran",
    label: "Quran Connections",
    icon: BookOpen,
    testid: "nav-quran-connections",
    accent: "amber",
    subtitle: "quran-connections",
  },
  {
    to: "/chess",
    label: "Chess Engine",
    icon: Gamepad2,
    testid: "nav-chess-engine",
    accent: "emerald",
    subtitle: "stockfish-linux",
  },
  {
    to: "/tasks",
    label: "Tasks",
    icon: CheckSquare,
    testid: "nav-tasks",
    accent: "violet",
  },
  {
    to: "/snippets",
    label: "Snippets",
    icon: FileCode,
    testid: "nav-snippets",
    accent: "cyan",
  },
  {
    to: "/admin",
    label: "Admin",
    icon: Shield,
    testid: "nav-admin",
    accent: "rose",
  },
];

const DOT_CLASS = {
  slate: "bg-slate-300",
  indigo: "bg-indigo-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  violet: "bg-violet-500",
  cyan: "bg-cyan-500",
  rose: "bg-rose-500",
};

export default function Sidebar() {
  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex md:w-72 lg:w-80 shrink-0 h-screen sticky top-0 border-r border-slate-200 bg-white flex-col"
    >
      <div className="px-6 py-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-slate-900 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight text-slate-900">
              FullEcosystem
            </div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">
              Unified workspace
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400 px-3 pb-3">
          Ecosystem
        </div>
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                [
                  "group flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-150",
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-100",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`h-1.5 w-1.5 rounded-full shrink-0 ${DOT_CLASS[item.accent]}`}
                    aria-hidden
                  />
                  <Icon
                    className={`h-4 w-4 shrink-0 ${isActive ? "text-white" : "text-slate-500"}`}
                    strokeWidth={1.75}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-tight">
                      {item.label}
                    </div>
                    {item.subtitle && (
                      <div
                        className={`font-mono text-[10px] truncate ${isActive ? "text-white/60" : "text-slate-400"}`}
                      >
                        {item.subtitle}
                      </div>
                    )}
                  </div>
                </>
              )}
            </NavLink>
          );
        })}

        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400 px-3 pt-6 pb-3">
          Account
        </div>
        <NavLink
          to="/settings"
          data-testid="nav-settings"
          className={({ isActive }) =>
            [
              "flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-150",
              isActive
                ? "bg-slate-900 text-white"
                : "text-slate-700 hover:bg-slate-100",
            ].join(" ")
          }
        >
          {({ isActive }) => (
            <>
              <Settings
                className={`h-4 w-4 ${isActive ? "text-white" : "text-slate-500"}`}
                strokeWidth={1.75}
              />
              <span className="text-sm font-medium">Settings</span>
            </>
          )}
        </NavLink>
      </nav>

      <div className="p-4 border-t border-slate-200">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
            Preview mode
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Deploy your merged ecosystem whenever you're ready — all modules
            share this workspace.
          </p>
        </div>
      </div>
    </aside>
  );
}
